import { memo, useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import type { Option } from "types";
import useActions from "features/users/useActions";
import useUserRoles from "features/users/useUserRoles";
import useMerchantActions from "features/merchants/useMerchantActions";
import { selectMerchantOptions } from "features/merchants/selectors";
import { selectDomainOptions } from "features/setups/selectors";
import { selectRoleOptions } from "features/users/selectors";
import { useTypedSelector } from "hooks";
import { BooleanSwitchValue } from "consts/general";
import { getOptionName } from "util/option";
import { getDomainsOptions, isDomainRequired, isMerchantRequired } from "ui/forms/User/helpers";

const DEFAULT_PASSWORD = 'Test1234!';

type Row = {
    email: string;
    firstName: string;
    lastName: string;
    passwd: string;
    passwordConfirmation: string;
    roleId: string | number | null;
    merchants: Option[];
    domains: Option[];
    twoFactorEnabled: number;
};

const makeRow = (email: string): Row => ({
    email,
    firstName: '',
    lastName: '',
    passwd: DEFAULT_PASSWORD,
    passwordConfirmation: DEFAULT_PASSWORD,
    roleId: null,
    merchants: [],
    domains: [],
    twoFactorEnabled: BooleanSwitchValue.Off
});

export type BulkCreateUsersProps = {
    fixedMerchant?: Option;
    merchantRolesOnly?: boolean;
};

const decodeLabel = (value: string) => {
    const element = document.createElement('textarea');
    element.innerHTML = value;
    return element.value;
};

const BulkCreateUsers = ({ fixedMerchant, merchantRolesOnly = false }: BulkCreateUsersProps) => {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [emailText, setEmailText] = useState('');
    const [rows, setRows] = useState<Row[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const allRoles = useTypedSelector(selectRoleOptions);
    const roles = merchantRolesOnly
        ? allRoles.filter(role => getOptionName(role).startsWith('Merchant'))
        : allRoles;
    const merchants = useTypedSelector(selectMerchantOptions).map((option: Option) => ({
        ...option,
        name: decodeLabel(option.name)
    }));
    const domains = useTypedSelector(selectDomainOptions);
    const actions = useActions();
    const merchantActions = useMerchantActions();
    const merchantActionsRef = useRef(merchantActions);
    merchantActionsRef.current = merchantActions;
    useUserRoles();

    useEffect(() => {
        if (open) merchantActionsRef.current.getAllMerchants();
    }, [open]);

    const emails = useMemo(() => Array.from(new Set(
        emailText.split(/[\s,;]+/).map(value => value.trim().toLowerCase()).filter(Boolean)
    )), [emailText]);
    const invalidEmails = emails.filter(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

    const close = () => {
        setOpen(false);
        setStep(0);
        setEmailText('');
        setRows([]);
        setError('');
    };
    const updateRow = (index: number, patch: Partial<Row>) => setRows(current =>
        current.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row)
    );

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            for (const row of rows) {
                const response = await actions.updateOrCreateUser({
                    email: row.email,
                    firstName: row.firstName,
                    lastName: row.lastName,
                    passwd: row.passwd,
                    roleId: row.roleId,
                    twoFactorEnabled: row.twoFactorEnabled
                } as any);
                const userId = response.data.coreId;
                await Promise.all([
                    ...row.merchants.map(({ id }) => actions.associateUserMerchant({ userId, merchantId: id })[0]),
                    ...row.domains.map(({ id }) => actions.associateUserMor({ userId, MOR: String(id) })[0])
                ]);
            }
            close();
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Some users could not be created.');
        } finally {
            setSaving(false);
        }
    };

    const merchantEnabled = (row: Row) => Boolean(row.roleId && isMerchantRequired(roles)(row.roleId));
    const morEnabled = (row: Row) => Boolean(row.roleId && isDomainRequired(roles)(row.roleId));
    const rowsInvalid = rows.some(row => !row.firstName || !row.lastName || !row.roleId ||
        (merchantEnabled(row) && !row.merchants.length) ||
        (morEnabled(row) && !row.domains.length) ||
        row.passwd.length < 8 || row.passwd !== row.passwordConfirmation);

    return (
        <>
            <Button size='small' startIcon={<GroupAddIcon />} onClick={() => setOpen(true)}>
                Bulk add users
            </Button>
            <Dialog open={open} onClose={saving ? undefined : close} fullWidth maxWidth='lg'>
                <DialogTitle>Bulk create users</DialogTitle>
                <DialogContent>
                    <Stepper activeStep={step} sx={{ mb: 3 }}>
                        <Step><StepLabel>Paste emails</StepLabel></Step>
                        <Step><StepLabel>User details</StepLabel></Step>
                    </Stepper>
                    {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
                    {step === 0 ? (
                        <TextField
                            autoFocus
                            fullWidth
                            multiline
                            minRows={8}
                            label='Email addresses'
                            placeholder={'user.one@example.com\nuser.two@example.com'}
                            helperText={`${emails.length} unique email address(es). Use newline, comma, semicolon or space.`}
                            error={Boolean(invalidEmails.length)}
                            value={emailText}
                            onChange={event => setEmailText(event.target.value)}
                        />
                    ) : (
                        <Box sx={{ display: 'grid', gap: 2, maxHeight: '65vh', overflow: 'auto', pr: 1 }}>
                            {rows.map((row, index) => (
                                <Box key={row.email} sx={{ display: 'grid', gridTemplateColumns: '1.4fr repeat(4, 1fr)', gap: 1, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    <TextField size='small' label='Email' value={row.email} disabled />
                                    <TextField size='small' required label='First name' value={row.firstName} onChange={event => updateRow(index, { firstName: event.target.value })} />
                                    <TextField size='small' required label='Last name' value={row.lastName} onChange={event => updateRow(index, { lastName: event.target.value })} />
                                    <TextField size='small' required type='password' label='Password' value={row.passwd} onChange={event => updateRow(index, { passwd: event.target.value })} />
                                    <TextField size='small' required type='password' label='Confirm password' error={row.passwd !== row.passwordConfirmation} value={row.passwordConfirmation} onChange={event => updateRow(index, { passwordConfirmation: event.target.value })} />
                                    <TextField select size='small' required label='Role' value={row.roleId ?? ''} onChange={event => updateRow(index, {
                                        roleId: event.target.value,
                                        merchants: fixedMerchant ? [fixedMerchant] : [],
                                        domains: []
                                    })}>
                                        {roles.map(option => <MenuItem key={String(option.id)} value={option.id}>{option.name}</MenuItem>)}
                                    </TextField>
                                    <Autocomplete<Option, true> multiple size='small' disabled={Boolean(fixedMerchant) || !merchantEnabled(row)} options={merchants as Option[]} getOptionLabel={(option: Option) => option.name} value={row.merchants} onChange={(_, value: Option[]) => updateRow(index, { merchants: value })} renderInput={params => <TextField {...params} required={merchantEnabled(row)} label='Merchants' />} />
                                    <Autocomplete<Option, true> multiple size='small' disabled={!morEnabled(row)} options={getDomainsOptions(roles, domains)({ roleId: row.roleId })} getOptionLabel={(option: Option) => option.name} value={row.domains} onChange={(_, value: Option[]) => updateRow(index, { domains: value })} renderInput={params => <TextField {...params} required={morEnabled(row)} label='MORs' />} />
                                    <FormControlLabel control={<Checkbox checked={Boolean(row.twoFactorEnabled)} onChange={(_, checked) => updateRow(index, { twoFactorEnabled: checked ? BooleanSwitchValue.On : BooleanSwitchValue.Off })} />} label='2FA enabled' />
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={step ? () => setStep(0) : close}>{step ? 'Back' : 'Cancel'}</Button>
                    {step === 0 ? (
                        <Button variant='contained' disabled={!emails.length || Boolean(invalidEmails.length)} onClick={() => {
                            setRows(emails.map(email => ({ ...makeRow(email), merchants: fixedMerchant ? [fixedMerchant] : [] })));
                            setStep(1);
                        }}>
                            Next
                        </Button>
                    ) : (
                        <LoadingButton variant='contained' loading={saving} disabled={rowsInvalid} onClick={save}>
                            Save {rows.length} users
                        </LoadingButton>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default memo(BulkCreateUsers);
